import React, { useState, useEffect } from 'react'
import { Shield, Edit, Save, X, AlertTriangle, Clock, User } from 'lucide-react'
import { useAdminApi } from '../contexts/AdminApiContext'
import RoleProtection from './RoleProtection'
import './RoleManagement.css'

interface UserWithRole {
  id: string
  email: string
  name: string
  role: string
  created_at: string
  updated_at: string
  role_assigned_at?: string
  role_assigned_by?: string
}

interface RoleChangeDialog {
  user: UserWithRole | null
  isOpen: boolean
  newRole: string
  reason: string
}

const ROLE_OPTIONS = [
  { value: 'user', label: 'User', description: 'Regular creator app access', color: 'gray' },
  { value: 'admin', label: 'Admin', description: 'Admin app access, data viewing', color: 'blue' },
  { value: 'super_admin', label: 'Super Admin', description: 'Full admin access, role management', color: 'purple' }
]

const RoleManagement: React.FC = () => {
  const [users, setUsers] = useState<UserWithRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [roleDialog, setRoleDialog] = useState<RoleChangeDialog>({
    user: null,
    isOpen: false,
    newRole: '',
    reason: ''
  })
  const [updating, setUpdating] = useState(false)

  const { apiService, isReady } = useAdminApi()

  useEffect(() => {
    if (isReady) {
      loadUsers()
    }
  }, [isReady])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)

    try {
      const userData = await apiService.getUsers()
      setUsers(userData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
      console.error('Failed to load users:', err)
    } finally {
      setLoading(false)
    }
  }

  const openRoleDialog = (user: UserWithRole) => {
    setRoleDialog({
      user,
      isOpen: true,
      newRole: user.role,
      reason: ''
    })
  }

  const closeRoleDialog = () => {
    setRoleDialog({
      user: null,
      isOpen: false,
      newRole: '',
      reason: ''
    })
  }

  const handleRoleUpdate = async () => {
    if (!roleDialog.user || !roleDialog.newRole) return

    setUpdating(true)

    try {
      await apiService.updateUserRole(
        roleDialog.user.id,
        roleDialog.newRole,
        roleDialog.reason || undefined
      )

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === roleDialog.user?.id 
          ? { ...user, role: roleDialog.newRole, role_assigned_at: new Date().toISOString() }
          : user
      ))

      closeRoleDialog()
    } catch (err) {
      console.error('Failed to update role:', err)
      alert(err instanceof Error ? err.message : 'Failed to update user role')
    } finally {
      setUpdating(false)
    }
  }

  const getRoleInfo = (role: string) => {
    return ROLE_OPTIONS.find(option => option.value === role) || ROLE_OPTIONS[0]
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="role-management-loading">
        <div className="loading-content">
          <Shield className="loading-spinner" size={32} />
          <span>Loading user roles...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="role-management-error">
        <AlertTriangle size={32} />
        <h3>Failed to Load Users</h3>
        <p>{error}</p>
        <button className="btn btn-secondary" onClick={loadUsers}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <RoleProtection requiredRole="super_admin" fallbackMessage="Super admin access required for role management">
      <div className="role-management">
        <div className="role-management-header">
          <div className="header-content">
            <Shield size={24} />
            <h2>Role Management</h2>
            <p>Manage user roles and permissions for the admin system</p>
          </div>
          <div className="header-stats">
            <div className="stat">
              <span className="stat-value">{users.length}</span>
              <span className="stat-label">Total Users</span>
            </div>
            <div className="stat">
              <span className="stat-value">{users.filter(u => u.role === 'admin' || u.role === 'super_admin').length}</span>
              <span className="stat-label">Admins</span>
            </div>
          </div>
        </div>

        <div className="users-table-container glassmorphism">
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Current Role</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => {
                const roleInfo = getRoleInfo(user.role)
                return (
                  <tr key={user.id} className="user-row">
                    <td className="user-info-cell">
                      <div className="user-info">
                        <div className="user-avatar">
                          <User size={20} />
                        </div>
                        <div className="user-details">
                          <div className="user-name">{user.name || 'Unnamed User'}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="role-cell">
                      <span className={`role-badge role-${user.role}`}>
                        {roleInfo.label}
                      </span>
                      <div className="role-description">{roleInfo.description}</div>
                    </td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Clock size={14} />
                        <span>{formatDate(user.role_assigned_at || user.created_at)}</span>
                      </div>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openRoleDialog(user)}
                        title="Change role"
                      >
                        <Edit size={14} />
                        Change Role
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Role Change Dialog */}
        {roleDialog.isOpen && roleDialog.user && (
          <div className="role-dialog-overlay">
            <div className="role-dialog glassmorphism">
              <div className="dialog-header">
                <h3>Change User Role</h3>
                <button className="close-btn" onClick={closeRoleDialog}>
                  <X size={20} />
                </button>
              </div>

              <div className="dialog-content">
                <div className="user-summary">
                  <div className="user-avatar">
                    <User size={24} />
                  </div>
                  <div>
                    <div className="user-name">{roleDialog.user.name || 'Unnamed User'}</div>
                    <div className="user-email">{roleDialog.user.email}</div>
                  </div>
                </div>

                <div className="current-role-info">
                  <label>Current Role:</label>
                  <span className={`role-badge role-${roleDialog.user.role}`}>
                    {getRoleInfo(roleDialog.user.role).label}
                  </span>
                </div>

                <div className="role-selector">
                  <label>New Role:</label>
                  <div className="role-options">
                    {ROLE_OPTIONS.map(option => (
                      <button
                        key={option.value}
                        className={`role-option ${roleDialog.newRole === option.value ? 'selected' : ''}`}
                        onClick={() => setRoleDialog(prev => ({ ...prev, newRole: option.value }))}
                        disabled={updating}
                      >
                        <span className={`role-badge role-${option.value}`}>
                          {option.label}
                        </span>
                        <span className="role-option-description">{option.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="reason-input">
                  <label htmlFor="reason">Reason for change:</label>
                  <textarea
                    id="reason"
                    value={roleDialog.reason}
                    onChange={(e) => setRoleDialog(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Optional: Provide a reason for this role change..."
                    disabled={updating}
                    rows={3}
                  />
                </div>
              </div>

              <div className="dialog-actions">
                <button
                  className="btn btn-secondary"
                  onClick={closeRoleDialog}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleRoleUpdate}
                  disabled={updating || roleDialog.newRole === roleDialog.user.role}
                >
                  {updating ? (
                    <>
                      <div className="spinner" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Update Role
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </RoleProtection>
  )
}

export default RoleManagement